import { Box, Button, FormControl, Grid, Typography } from "@mui/material";
import { Variant } from "@mui/material/styles/createTypography";
import { get, pullAt } from "lodash";
import { useConfirm } from "material-ui-confirm";
import { FC, useContext, useEffect, useRef } from "react";
import usePractitioner from "../../hooks/usePractitioner";
import { formatDateTime } from "../../utils/DateUtils";
import DictationControl from "../dictation/DictationControl";
import DeleteIconButton from "../widgets/buttons/DeleteIconButton";
import {
  DictationValue,
  DictactionContext,
} from "../../contexts/DictationProvider";
import { useUnsavedPageWarning } from "../../contexts/UnsavedPageWarningContext";
import { createReferenceV5 } from "../../utils/DataTransformationUtils";

interface NotesInputProps {
  value: any;
  onChange: any;
  label?: string;
  placeholderText: string;
  buttonLabel?: string;
  inputSize?: "small" | "medium";
  labelVariant?: Variant;
  disabled: boolean;
  minRows?: number;
  required?: boolean;
  maxNotes?: number;
  autoFocus?: boolean;
  newFormat?: boolean;
  name?: string;
}

const NotesInput: FC<NotesInputProps> = ({
  value,
  onChange,
  label,
  placeholderText,
  buttonLabel,
  labelVariant,
  inputSize,
  disabled,
  maxNotes,
  minRows = 1,
  required,
  autoFocus = true,
  newFormat = false,
  name,
}) => {
  const confirm = useConfirm();
  const lastInputRef = useRef<HTMLInputElement | null>(null);
  const practitionerReference = usePractitioner();
  const { isDictationActive } = useUnsavedPageWarning();
  const { stopActiveDictation } = useContext(
    DictactionContext,
  ) as DictationValue;

  // Focus on the last input field when value changes (new note added) or component mounts
  useEffect(() => {
    if (autoFocus && lastInputRef.current && !disabled) {
      // Small delay to ensure the DOM is updated
      setTimeout(() => {
        lastInputRef.current?.focus();
      }, 100);
    }
  }, [value?.length, disabled, autoFocus]);

  const hasNotes = Boolean(value && value.length > 0);

  if (disabled && !hasNotes) {
    return null;
  }

  const addNote = () => {
    stopActiveDictation?.();
    // Short delay so the engine can flush the final transcript before the list updates
    const delayMs = 180;
    setTimeout(() => {
      if (value && value.length > 0) {
        onChange(
          newFormat
            ? [
                ...value,
                {
                  authorReference: createReferenceV5(
                    "Practitioner",
                    practitionerReference.identifier,
                    practitionerReference.display,
                  ),
                },
              ]
            : [
                ...value,
                {
                  author: createReferenceV5(
                    "Practitioner",
                    practitionerReference.identifier,
                    practitionerReference.display,
                  ),
                },
              ],
        );
      } else {
        onChange(
          newFormat
            ? [
                {
                  authorReference: createReferenceV5(
                    "Practitioner",
                    practitionerReference.identifier,
                    practitionerReference.display,
                  ),
                },
              ]
            : [{ author: practitionerReference }],
        );
      }
    }, delayMs);
  };

  const deleteNote = (index) => {
    confirm({ description: "This action is permanent once saved!" })
      .then(() => {
        if (index > -1) {
          stopActiveDictation?.();
          const copy = [...value];
          pullAt(copy, [index]);
          // Short delay so the final transcript is committed before the note is removed
          setTimeout(() => onChange(copy), 180);
        }
      })
      .catch(() => {});
  };

  return (
    <Grid container spacing={1}>
      {maxNotes !== 1 && label && (
        <Grid item xs={12}>
          <Typography variant={labelVariant || "subtitle2"}>{label}</Typography>
        </Grid>
      )}

      {value?.map((note, i) => (
        <Grid item xs={12} key={`note-${i}`}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="start"
            flexDirection="row"
            gap={1}
          >
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={maxNotes === 1 ? 12 : 11}>
                  <FormControl variant="outlined" fullWidth>
                    <DictationControl
                      fullWidth
                      size={inputSize}
                      required={required}
                      name={name ? `${name}-${i}` : `notes-${i}`}
                      value={get(note, "text") || ""}
                      onTextChange={(val) => {
                        const updatedNotes = [...value];
                        updatedNotes[i].text = val;
                        updatedNotes[i].time = new Date();
                        onChange(updatedNotes);
                      }}
                      id={`notes-label-${i}`}
                      label={maxNotes === 1 ? label : ""}
                      disabled={
                        (maxNotes !== 1 &&
                          practitionerReference?.reference !==
                            value[i]?.author?.reference &&
                          practitionerReference?.reference !==
                            value[i]?.authorReference?.reference) ||
                        disabled ||
                        i < value.length - 1
                      }
                      placeholder={placeholderText}
                      minRows={minRows}
                      maxRows={12}
                      inputRef={
                        i === value.length - 1 ? lastInputRef : undefined
                      }
                    />
                  </FormControl>
                </Grid>
                {(!maxNotes || maxNotes > 1) && (
                  <Grid item xs={maxNotes === 1 ? 12 : 1}>
                    {!disabled &&
                      !isDictationActive &&
                      practitionerReference?.reference ===
                        value[i]?.author?.reference &&
                      i === value.length - 1 && (
                        <DeleteIconButton
                          tooltip="Remove Comment"
                          onClick={() => deleteNote(i)}
                          disabled={disabled}
                        />
                      )}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexDirection="row"
            gap={1}
          >
            <Typography
              flex={1}
              style={{ float: "right" }}
              align="right"
              variant="caption"
            >
              {(
                newFormat
                  ? get(value[i], "authorReference.display")
                  : get(value[i], "author.display")
              )
                ? `Author: ${newFormat ? get(value[i], "authorReference.display") : get(value[i], "author.display")}`
                : ""}{" "}
              {get(value[i], "time")
                ? `Time: ${formatDateTime(new Date(get(value[i], "time")))}`
                : ""}
            </Typography>
            <Box width="40px" />
          </Box>
        </Grid>
      ))}

      {(!maxNotes || maxNotes > 1) && !disabled && (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-start">
            <Button
              onClick={addNote}
              color="info"
              variant="outlined"
              size="small"
            >
              {buttonLabel || "Add Comment"}
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default NotesInput;
